import { faClipboardCheck } from '@fortawesome/pro-light-svg-icons';
import { Option } from '@spike/model';
import { FunctionComponent, useState } from 'react';
import EditSetting from './EditSetting';
import { useDispatch, useSelector } from 'react-redux';
import { useSettingsApiClientWrapper } from 'hooks';
import { CustomContentNotification } from '@spike/customcontentnotification-model';
import { OverFullWindow, Spinner } from 'components/UI';
import EditNotificationContent from './EditCustomContentNotification/EditNotificationContent';
import {
    CustomContentNotificationState,
    CustomContentNotificationStatus,
    updateOrCreateCustomContentNotification
} from '@spike/custom-content-notification-action';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';

export interface Props {
    onEdit?: (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>, optionId: string) => void;
    loading: boolean;
}

export const EditConfirmation: FunctionComponent<Props> = props => {
    const key = 'client_confirmation_request';
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();
    const [editContentKey, setEditContentKey] = useState<string>('');
    const [customContentNotification, setCustomContentNotification] = useState<CustomContentNotification>({
        key: '',
        content: '',
        name: '',
        marketplaceID: 0
    });
    const [loading, setLoading] = useState(false);

    const customContentNotificationState = useSelector<RootState, CustomContentNotificationState>(
        state => state.customContentNotifications
    );

    const noticeOptions = [
        { id: '24', name: '1 day before' },
        { id: '48', name: '2 days before' },
        { id: '72', name: '3 days before' },
        { id: '96', name: '4 days before' },
        { id: '120', name: '5 days before' },
        { id: '144', name: '6 days before' },
        { id: '168', name: '7 days before' }
    ];

    const handleEdit = (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>) => {
        props.onEdit && props.onEdit(emailChecked, smsChecked, noticeSelected, key);
    };

    const handleSave = () => {
        setLoading(true);
        setEditContentKey('');
        dispatch(updateOrCreateCustomContentNotification(apiClientWrapper, customContentNotification));
    };

    useNonInitialEffect(() => {
        if (
            customContentNotificationState.status === CustomContentNotificationStatus.UpdateSuccess &&
            customContentNotificationState.customContentNotification
        ) {
            setCustomContentNotification(customContentNotificationState.customContentNotification);
            setLoading(false);
        }
    }, [customContentNotificationState.status]);

    const subtitle = <>Requests unconfirmed clients to confirm their upcoming appointment.</>;

    return editContentKey !== '' ? (
        <OverFullWindow
            hideHeader={false}
            showFooter={true}
            title={editContentKey.includes('email') ? 'Edit Email' : 'Preview SMS'}
            centered={true}
            onClose={() => setEditContentKey('')}
            onBack={() => setEditContentKey('')}
            onSave={() => handleSave()}
        >
            <EditNotificationContent
                notificationKey={editContentKey}
                title={'Confirmation Request'}
                subtitle={'Requests unconfirmed clients to confirm their upcoming appointment.'}
                headerIcon={faClipboardCheck}
                sectionSubtitle={'Personalize confirmation requests to ensure appointments are secured.'}
                customContentNotification={customContentNotification}
                setCustomContentNotification={setCustomContentNotification}
            />
        </OverFullWindow>
    ) : loading ? (
        <Spinner />
    ) : (
        <EditSetting
            optionId={key}
            noticeOptions={noticeOptions}
            headerIcon={faClipboardCheck}
            title={'Confirmation Request'}
            subtitle={subtitle}
            defaultOption={noticeOptions[1]}
            loading={props.loading}
            onEdit={handleEdit}
            showEditContent={true}
            onEditContent={setEditContentKey}
        />
    );
};

export default EditConfirmation;
